import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardApi, unitsApi, summaryApi, driverFilesApi, vehicleAlertsApi, complianceApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Truck, 
  Users, 
  Package, 
  DollarSign, 
  TrendingUp,
  ArrowRight,
  Calendar,
  FileText,
  CalendarRange,
  Download,
  X,
  AlertTriangle,
  Clock,
  CheckCircle,
  Map,
  Send,
} from 'lucide-react';
import { formatCurrency, getWeekStart, getWeekRange } from '../lib/utils';
import UnitSelectItems from '../components/UnitSelectItems';
import { toast } from 'sonner';
import { KpiStrip } from '../components/dashboard/KpiStrip';
import { RevenueTrendCard } from '../components/dashboard/RevenueTrendCard';
import { RevenueSplitCard } from '../components/dashboard/RevenueSplitCard';
import { TopUnitsCard } from '../components/dashboard/TopUnitsCard';
import { TopBrokersCard } from '../components/dashboard/TopBrokersCard';
import { DashCard } from '../components/dashboard/DashCard';
import { FuelCard } from '../components/dashboard/FuelCard';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});
const greenIcon = new L.Icon({ iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png', shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png', iconSize: [18, 30], iconAnchor: [9, 30], popupAnchor: [1, -24], shadowSize: [30, 30] });
const redIcon = new L.Icon({ iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png', shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png', iconSize: [18, 30], iconAnchor: [9, 30], popupAnchor: [1, -24], shadowSize: [30, 30] });
const blueIcon = new L.Icon({ iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png', shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png', iconSize: [18, 30], iconAnchor: [9, 30], popupAnchor: [1, -24], shadowSize: [30, 30] });

export default function Dashboard() {
  const { isDriver, user, canViewWeeklySummary } = useAuth();
  const [overview, setOverview] = useState(null);
  const [fuel, setFuel] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Invoice generation state
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState('');
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [dateRangeDialogOpen, setDateRangeDialogOpen] = useState(false);
  const [invoiceStartDate, setInvoiceStartDate] = useState('');
  const [invoiceEndDate, setInvoiceEndDate] = useState('');
  
  // PDF Viewer state
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [itemizedDeductions, setItemizedDeductions] = useState(false);
  
  // Driver-specific data
  const [driverUnitSummary, setDriverUnitSummary] = useState(null);
  
  // Expiring documents notification state
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [expiringAlerts, setExpiringAlerts] = useState([]);
  const [showExpiringNotification, setShowExpiringNotification] = useState(false);
  const [mapData, setMapData] = useState(null);
  const [dismissedExpiring, setDismissedExpiring] = useState(() => {
    const dismissed = localStorage.getItem('dismissedExpiringDocs');
    return dismissed ? JSON.parse(dismissed) : [];
  });

  useEffect(() => {
    loadData();
  }, []);

  // Show expiring notification periodically
  useEffect(() => {
    const allExpiring = [...expiringDocs, ...expiringAlerts];
    const undismissed = allExpiring.filter(item => !dismissedExpiring.includes(item.id));
    
    if (undismissed.length > 0 && !isDriver) {
      // Show notification after a short delay
      const timer = setTimeout(() => {
        setShowExpiringNotification(true);
      }, 2000);
      
      // Auto-hide after 10 seconds
      const hideTimer = setTimeout(() => {
        setShowExpiringNotification(false);
      }, 12000);
      
      return () => {
        clearTimeout(timer);
        clearTimeout(hideTimer);
      };
    }
  }, [expiringDocs, expiringAlerts, dismissedExpiring, isDriver]);

  const loadData = async () => {
    const isStaff = user?.role !== 'driver';
    // Secondary widgets load in parallel and never block the main stats render.
    const secondary = isStaff ? Promise.allSettled([
      Promise.all([driverFilesApi.getAll({}), vehicleAlertsApi.getAll({})]).then(([driverFilesRes, vehicleAlertsRes]) => {
        const today = new Date();
        const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
        const expiring = driverFilesRes.data.filter(f => {
          if (!f.expiration_date) return false;
          const expDate = new Date(f.expiration_date);
          return expDate <= thirtyDaysFromNow && expDate >= today;
        }).map(f => ({
          id: f.id,
          type: 'driver_file',
          title: f.title,
          expiration_date: f.expiration_date,
          category: f.file_type
        }));
        const urgentAlerts = vehicleAlertsRes.data.filter(a => {
          if (a.status === 'completed') return false;
          return new Date(a.due_date) <= thirtyDaysFromNow;
        }).map(a => ({
          id: a.id,
          type: 'vehicle_alert',
          title: a.title,
          expiration_date: a.due_date,
          category: a.alert_type,
          status: a.status
        }));
        setExpiringDocs(expiring);
        setExpiringAlerts(urgentAlerts);
      }),
      complianceApi.getFleetMap().then((r) => setMapData(r.data)),
      dashboardApi.getFuel().then((r) => setFuel(r.data)).catch(() => setFuel({ units: [], weeks: [], fleet: {} })),
    ]) : Promise.resolve();
    try {
      const [overviewRes, unitsRes] = await Promise.all([
        isStaff ? dashboardApi.getOverview() : Promise.resolve({ data: null }),
        unitsApi.getAll()
      ]);
      setOverview(overviewRes.data);
      setUnits(unitsRes.data || []);
      
      // If driver, fetch their unit's weekly summary
      if (user?.role === 'driver' && user?.assigned_unit_id) {
        try {
          const summaryRes = await summaryApi.getForUnit(user.assigned_unit_id, getWeekStart());
          setDriverUnitSummary(summaryRes.data);
        } catch (err) {
          console.log('No summary data for driver unit yet');
        }
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Could not load dashboard figures. Please refresh.');
    } finally {
      setLoading(false);
    }
    await secondary;
  };

  const currentWeek = getWeekStart();

  // Invoice generation handlers
  const handleGenerateInvoice = async () => {
    if (!selectedUnit) {
      toast.error('Please select a unit first');
      return;
    }
    
    setGeneratingInvoice(true);
    try {
      const res = await summaryApi.exportPdf(selectedUnit, currentWeek, itemizedDeductions);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfBlob(blob);
      setPdfMode('weekly');
      setPdfViewerOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const openDateRangeDialog = () => {
    if (!selectedUnit) {
      toast.error('Please select a unit first');
      return;
    }
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    setInvoiceStartDate(firstDayOfMonth.toISOString().split('T')[0]);
    setInvoiceEndDate(today.toISOString().split('T')[0]);
    setDateRangeDialogOpen(true);
  };

  const handleGenerateCustomInvoice = async () => {
    if (!invoiceStartDate || !invoiceEndDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    
    setGeneratingInvoice(true);
    setDateRangeDialogOpen(false);
    try {
      const res = await summaryApi.generateInvoiceCustom(selectedUnit, invoiceStartDate, invoiceEndDate, null, itemizedDeductions);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfBlob(blob);
      setPdfMode('custom');
      setPdfCustomRange({ start: invoiceStartDate, end: invoiceEndDate });
      setPdfViewerOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const handleDownloadPdf = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `Settlement_Pay.pdf`;
      link.click();
    }
  };

  // Track which PDF is currently displayed (weekly or custom range) so the
  // Email Settlement button in the PDF viewer sends the RIGHT PDF.
  const [pdfMode, setPdfMode] = useState('weekly'); // 'weekly' | 'custom'
  const [pdfCustomRange, setPdfCustomRange] = useState({ start: '', end: '' });
  // Keep the ACTUAL PDF blob the user is viewing so Email Settlement sends the
  // exact same file — no risk of a server-side re-generation producing a
  // different (e.g. empty) PDF if data changed after the preview was generated.
  const [pdfBlob, setPdfBlob] = useState(null);

  // Server-side one-click Email Settlement — sends the currently-viewed PDF
  // to the assigned owner-operator on file for the selected unit.
  const [emailingSettlement, setEmailingSettlement] = useState(false);
  const handleEmailSettlement = async () => {
    if (!selectedUnit) {
      toast.error('Please select a unit first');
      return;
    }
    if (!pdfBlob) {
      toast.error('Generate the PDF first, then click Email Settlement.');
      return;
    }
    setEmailingSettlement(true);
    try {
      const periodLabel = pdfMode === 'custom' && pdfCustomRange.start && pdfCustomRange.end
        ? `${pdfCustomRange.start} to ${pdfCustomRange.end}`
        : `Week of ${getWeekRange(currentWeek)}`;
      const filename = `Settlement_Pay_${getUnitNumber(selectedUnit)}_${
        pdfMode === 'custom' ? `${pdfCustomRange.start}_to_${pdfCustomRange.end}` : currentWeek
      }.pdf`;
      const res = await summaryApi.emailSettlementAttachment(
        selectedUnit, pdfBlob, filename, periodLabel, null, null, ''
      );
      toast.success(`Settlement emailed to ${res.data?.sent_to || 'owner-operator'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send settlement email');
    } finally {
      setEmailingSettlement(false);
    }
  };

  const closePdfViewer = () => {
    setPdfViewerOpen(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setPdfBlob(null);
  };

  const getUnitNumber = (unitId) => {
    const unit = units.find(u => u.id === unitId);
    return unit ? unit.unit_number : unitId;
  };

  const dismissExpiringItem = (itemId) => {
    const newDismissed = [...dismissedExpiring, itemId];
    setDismissedExpiring(newDismissed);
    localStorage.setItem('dismissedExpiringDocs', JSON.stringify(newDismissed));
  };

  const dismissAllExpiring = () => {
    const allIds = [...expiringDocs, ...expiringAlerts].map(item => item.id);
    setDismissedExpiring(allIds);
    localStorage.setItem('dismissedExpiringDocs', JSON.stringify(allIds));
    setShowExpiringNotification(false);
  };

  const getDaysUntil = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    return Math.ceil((date - today) / (1000 * 60 * 60 * 24));
  };

  const undismissedExpiring = [...expiringDocs, ...expiringAlerts].filter(
    item => !dismissedExpiring.includes(item.id)
  );

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-10 w-72 rounded-xl" />
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-[104px] rounded-2xl" />
          ))}
        </div>
        <Skeleton className="h-[380px] rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Expiring Documents Notification Popup */}
      {showExpiringNotification && undismissedExpiring.length > 0 && !isDriver && (
        <div className="fixed top-20 left-4 right-4 sm:left-auto z-50 sm:w-96 min-w-0 animate-in slide-in-from-right duration-300">
          <Card className="border-blue-300 bg-blue-50 dark:bg-blue-950/90 shadow-lg">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-manrope flex items-center gap-2 text-blue-800 dark:text-blue-300">
                  <AlertTriangle className="h-5 w-5" />
                  Attention Required
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowExpiringNotification(false)} data-testid="expiring-popup-close">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-blue-700 dark:text-blue-400">
                {undismissedExpiring.length} document{undismissedExpiring.length > 1 ? 's' : ''} expiring soon:
              </p>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {undismissedExpiring.slice(0, 5).map((item) => {
                  const days = getDaysUntil(item.expiration_date);
                  const isOverdue = days < 0;
                  return (
                    <div 
                      key={item.id} 
                      className={`flex items-center justify-between p-2 rounded-lg ${
                        isOverdue ? 'bg-slate-200 dark:bg-slate-800' : 'bg-blue-100 dark:bg-blue-900/50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Clock className={`h-4 w-4 ${isOverdue ? 'text-slate-700 dark:text-slate-200' : 'text-blue-600'}`} />
                        <div>
                          <p className="text-sm font-medium text-foreground">{item.title}</p>
                          <p className={`text-xs ${isOverdue ? 'text-slate-700 dark:text-slate-300 font-semibold' : 'text-blue-600'}`}>
                            {isOverdue ? `Overdue by ${Math.abs(days)} days` : `${days} days left`}
                          </p>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => dismissExpiringItem(item.id)}
                        className="h-6 w-6 p-0"
                      >
                        <CheckCircle className="h-4 w-4 text-blue-600" />
                      </Button>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2 pt-2">
                <Link to="/driver-files" className="flex-1">
                  <Button size="sm" variant="outline" className="w-full dark:bg-slate-900 dark:text-slate-100 dark:border-slate-600" data-testid="expiring-popup-view-all">View All</Button>
                </Link>
                <Button size="sm" variant="outline" onClick={dismissAllExpiring} className="dark:bg-slate-900 dark:text-slate-100 dark:border-slate-600" data-testid="expiring-popup-dismiss-all">
                  Dismiss All
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Expiring Documents Alert Banner (Persistent) */}
      {undismissedExpiring.length > 0 && !isDriver && !showExpiringNotification && (
        <button
          type="button"
          className="w-full flex items-center justify-between gap-3 rounded-2xl border border-blue-200/80 bg-blue-50/70 dark:bg-blue-950/30 dark:border-blue-900/60 px-4 py-2.5 text-left hover:bg-blue-50 dark:hover:bg-blue-950/50 transition-colors"
          onClick={() => setShowExpiringNotification(true)}
          data-testid="expiring-docs-banner"
        >
          <span className="flex items-center gap-3 text-sm">
            <AlertTriangle className="h-4 w-4 text-blue-600" />
            <span className="font-semibold text-slate-800 dark:text-slate-100">
              {undismissedExpiring.length} document{undismissedExpiring.length > 1 ? 's' : ''} need attention
            </span>
            <span className="hidden sm:inline text-slate-500">Expiring licenses and vehicle documents</span>
          </span>
          <span className="flex items-center text-xs font-semibold text-blue-700 dark:text-blue-400">
            Review <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </span>
        </button>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 dash-rise">
        <div>
          {!isDriver && (
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-600 dark:text-blue-400">Fleet overview</p>
          )}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-manrope font-bold tracking-tight text-slate-900 dark:text-white mt-1">
            {isDriver ? 'Dashboard' : `Welcome back${user?.name ? `, ${user.name.split(' ')[0]}` : ''}`}
          </h1>
          <p className="text-base text-slate-600 dark:text-slate-300 mt-1.5" data-testid="dashboard-week-label">
            {isDriver ? (
              <>Welcome, {user?.name}. Viewing Unit: {user?.assigned_unit_number || 'Not assigned'}</>
            ) : (
              <>Week of {getWeekRange(currentWeek)}</>
            )}
          </p>
        </div>
        {!isDriver && (
          <Link to="/loads">
            <Button className="rounded-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 text-white px-5 shadow-sm transition-colors" data-testid="add-load-btn">
              <Package className="h-4 w-4 mr-2" />
              Add Load
            </Button>
          </Link>
        )}
      </div>

      {/* KPI strip + charts (staff) */}
      {!isDriver && overview && (
        <>
          <KpiStrip kpis={overview.kpis} />
          <RevenueTrendCard series={overview.series} last30={overview.last_30} />
          <FuelCard data={fuel} />
          <RevenueSplitCard last30={overview.last_30} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TopUnitsCard units={overview.top_units} />
            <TopBrokersCard brokers={overview.top_brokers} />
          </div>
        </>
      )}

      {/* Driver-specific welcome message and stats */}
      {isDriver && (
        <>
          <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-700 rounded-xl">
                  <Truck className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-blue-900 dark:text-blue-100">
                    Welcome, {user?.name}
                  </h2>
                  <p className="text-blue-700 dark:text-blue-300">
                    Unit: {user?.assigned_unit_number || 'Not Assigned'} • Week of {getWeekRange(currentWeek)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Driver Stats - Only their unit's data */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="hover:shadow-md transition-all duration-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Loads This Week</p>
                    <p className="text-3xl font-bold font-manrope mt-1">
                      {driverUnitSummary?.total_loads || 0}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      For Unit {user?.assigned_unit_number}
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-blue-500">
                    <Package className="h-6 w-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-md transition-all duration-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Net Payout This Week</p>
                    <p className="text-3xl font-bold font-manrope mt-1 text-blue-700 dark:text-blue-400">
                      {formatCurrency(driverUnitSummary?.net_payout || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      For Unit {user?.assigned_unit_number}
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-blue-700">
                    <DollarSign className="h-6 w-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Driver Quick Links */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-manrope">Quick Links</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {canViewWeeklySummary && (
                  <Link to="/summary" className="block">
                    <Button variant="outline" className="w-full justify-between h-auto py-4">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-blue-600" />
                        <span>View Weekly Summary</span>
                      </div>
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                )}
                <Link to="/driver-files" className="block">
                  <Button variant="outline" className="w-full justify-between h-auto py-4">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-blue-600" />
                      <span>Upload Documents</span>
                    </div>
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Operations row — Fleet map + invoice + quick actions (staff) */}
      {!isDriver && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {mapData && (mapData.loads?.length > 0 || mapData.drivers?.length > 0) ? (
            <DashCard
              title="Fleet Map"
              subtitle="This week's lanes and driver locations"
              icon={Map}
              delay={380}
              className="xl:col-span-2"
              testid="fleet-map-card"
              action={
                <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-300">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Pickup</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> Delivery</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500 inline-block" /> Drivers</span>
                </div>
              }
            >
              <div className="h-[340px] rounded-xl overflow-hidden border border-slate-200/70 dark:border-slate-800 isolate">
              <MapContainer
                center={(() => {
                  const coords = [];
                  mapData.loads?.forEach(l => { if (l.pickup_coords) coords.push(l.pickup_coords); if (l.delivery_coords) coords.push(l.delivery_coords); });
                  mapData.drivers?.forEach(d => { if (d.coords) coords.push(d.coords); });
                  return coords.length > 0 ? [coords.reduce((s, c) => s + c[0], 0) / coords.length, coords.reduce((s, c) => s + c[1], 0) / coords.length] : [39.8, -98.5];
                })()}
                zoom={4}
                style={{ height: '100%', width: '100%' }}
                dragging={false}
                zoomControl={false}
                scrollWheelZoom={false}
                touchZoom={false}
                doubleClickZoom={false}
                attributionControl={false}
                keyboard={false}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution=""
                />
                {mapData.drivers?.map((d, i) => d.coords && (
                  <Marker key={`d-${i}`} position={d.coords} icon={blueIcon}><Popup>{d.name}</Popup></Marker>
                ))}
                {mapData.loads?.map((l, i) => (
                  <React.Fragment key={`l-${i}`}>
                    {l.pickup_coords && <Marker position={l.pickup_coords} icon={greenIcon}><Popup>{l.pickup_location}</Popup></Marker>}
                    {l.delivery_coords && <Marker position={l.delivery_coords} icon={redIcon}><Popup>{l.delivery_location}</Popup></Marker>}
                    {l.pickup_coords && l.delivery_coords && (
                      <Polyline positions={[l.pickup_coords, l.delivery_coords]} pathOptions={{ color: '#3b82f6', weight: 2, opacity: 0.4, dashArray: '6 4' }} />
                    )}
                  </React.Fragment>
                ))}
              </MapContainer>
              </div>
            </DashCard>
          ) : (
            <DashCard title="Fleet Map" subtitle="No lanes this week yet" icon={Map} delay={380} className="xl:col-span-2" testid="fleet-map-card">
              <div className="h-[340px] rounded-xl bg-slate-50 dark:bg-slate-800/40 flex items-center justify-center text-sm text-slate-400">
                Lanes appear here as loads are added this week
              </div>
            </DashCard>
          )}

          <div className="flex flex-col gap-6">
            <DashCard title="Quick Invoice" subtitle="Settlement PDF for any unit" icon={FileText} delay={440} testid="quick-invoice-card">
              <div className="space-y-3">
                <Label htmlFor="invoice-unit" className="text-sm text-slate-600 dark:text-slate-300">Select unit</Label>
                <Select value={selectedUnit} onValueChange={setSelectedUnit}>
                  <SelectTrigger id="invoice-unit" className="rounded-xl" data-testid="invoice-unit-select">
                    <SelectValue placeholder="Choose a unit..." />
                  </SelectTrigger>
                  <SelectContent>
                    <UnitSelectItems
                      units={units}
                      renderExtra={(unit) => unit.assigned_driver_name ? ` (${unit.assigned_driver_name})` : ''}
                    />
                  </SelectContent>
                </Select>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    onClick={handleGenerateInvoice}
                    disabled={!selectedUnit || generatingInvoice}
                    className="rounded-full bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="generate-this-week-btn"
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    {generatingInvoice ? 'Generating...' : 'This Week'}
                  </Button>
                  <Button
                    onClick={openDateRangeDialog}
                    disabled={!selectedUnit || generatingInvoice}
                    variant="outline"
                    className="rounded-full border-slate-300 dark:border-slate-700"
                    data-testid="generate-custom-range-btn"
                  >
                    <CalendarRange className="h-4 w-4 mr-2" />
                    Custom Range
                  </Button>
                </div>
                {units.length === 0 && (
                  <p className="text-xs text-slate-500">
                    No units found. <Link to="/units" className="text-blue-600 hover:underline">Create a unit</Link> to generate invoices.
                  </p>
                )}
              </div>
            </DashCard>

            <DashCard title="Quick Actions" delay={500} testid="quick-actions-card">
              <div className="grid grid-cols-2 gap-2">
                {[
                  { to: '/units', label: 'Units', icon: Truck, testid: 'quick-units' },
                  { to: '/drivers', label: 'Drivers', icon: Users, testid: 'quick-drivers' },
                  { to: '/summary', label: 'Weekly Summary', icon: DollarSign, testid: 'quick-summary' },
                  { to: '/history', label: 'History', icon: TrendingUp, testid: 'quick-history' },
                ].map((q) => (
                  <Link
                    key={q.to}
                    to={q.to}
                    className="group flex items-center justify-between rounded-xl border border-slate-200/70 dark:border-slate-800 px-3 py-3.5 text-[15px] hover:border-blue-300 hover:bg-blue-50/60 dark:hover:bg-blue-950/30 transition-colors"
                    data-testid={q.testid}
                  >
                    <span className="flex items-center gap-2 font-medium text-slate-700 dark:text-slate-200">
                      <q.icon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      {q.label}
                    </span>
                    <ArrowRight className="h-3.5 w-3.5 text-slate-400 group-hover:translate-x-0.5 group-hover:text-blue-600 transition-transform" />
                  </Link>
                ))}
              </div>
            </DashCard>
          </div>
        </div>
      )}

      {/* Custom Date Range Dialog - only show for non-drivers */}
      {!isDriver && (
      <Dialog open={dateRangeDialogOpen} onOpenChange={setDateRangeDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="font-manrope flex items-center gap-2">
              <CalendarRange className="h-5 w-5 text-blue-600" />
              Custom Date Range Invoice
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Customize the date range for <strong>{getUnitNumber(selectedUnit)}</strong>'s settlement PDF.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dash-invoice-start-date">Start Date</Label>
                <Input
                  id="dash-invoice-start-date"
                  type="date"
                  value={invoiceStartDate}
                  onChange={(e) => setInvoiceStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dash-invoice-end-date">End Date</Label>
                <Input
                  id="dash-invoice-end-date"
                  type="date"
                  value={invoiceEndDate}
                  onChange={(e) => setInvoiceEndDate(e.target.value)}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Set both dates to the same value for a single week settlement.
            </p>
            <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/30">
              <Checkbox 
                id="dash-itemized" 
                checked={itemizedDeductions} 
                onCheckedChange={setItemizedDeductions}
                data-testid="dash-itemized-checkbox"
              />
              <Label htmlFor="dash-itemized" className="text-sm cursor-pointer">Itemized Deductions</Label>
              <span className="text-xs text-muted-foreground ml-auto">Show individual entries</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDateRangeDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleGenerateCustomInvoice}
              disabled={!invoiceStartDate || !invoiceEndDate}
            >
              <FileText className="h-4 w-4 mr-2" />
              Generate Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      {/* PDF Viewer Dialog - only show for non-drivers */}
      {!isDriver && (
      <Dialog open={pdfViewerOpen} onOpenChange={closePdfViewer}>
        <DialogContent className="sm:max-w-[90vw] sm:max-h-[95vh] p-0">
          <div className="flex items-center justify-between p-4 border-b">
            <DialogTitle className="font-manrope flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Settlement Pay - {getUnitNumber(selectedUnit)}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleEmailSettlement}
                disabled={emailingSettlement}
                className="border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-500/40 dark:text-blue-300 dark:hover:bg-blue-950/30"
                data-testid="email-settlement-btn"
              >
                {emailingSettlement ? (
                  <span className="animate-spin h-4 w-4 border-2 border-blue-600 dark:border-blue-300 border-t-transparent rounded-full mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                {emailingSettlement ? 'Sending...' : 'Email Settlement'}
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownloadPdf}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
              <Button variant="ghost" size="sm" onClick={closePdfViewer}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="p-4">
            {pdfUrl && (
              <iframe
                src={pdfUrl}
                className="w-full h-[calc(90vh-120px)] border rounded-lg"
                title="Settlement Pay PDF"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
      )}

    </div>
  );
}
